import { Row } from "@hightouch/htui";

import { styleSelectionLabel } from "src/pages/ad-studio/components/style-selection-label";
import type { ComposerSendSettings } from "src/pages/projects/amp-composer/composer-context";
import type { ComposerSelections } from "src/pages/projects/amp-composer/composer-selections";
import { emptyOptionsTooltip } from "src/pages/projects/amp-composer/empty-options-tooltip";
import {
  type ComposerOptions,
  type SelectedOptions,
  styleSelectionOf,
} from "src/pages/projects/amp-composer/use-composer-options";
import type { BrandScope } from "src/utils/brand-scope";
import {
  AdsPicker,
  EmailPicker,
} from "src/pages/projects/library/canvas/canvas-creative-pickers";

type ComposerControlsProps = {
  options: ComposerOptions;
  selected: SelectedOptions;
  sendSettings: ComposerSendSettings;
  brandScope: BrandScope;
  isLoading: boolean;
  onChange: (change: Partial<ComposerSelections>) => void;
};

/** The Ads and Email pickers beside the composer, one for each channel the person can make. */
export function ComposerControls({
  options,
  selected,
  sendSettings: { channels, isCreativeBoostEnabled, isVideoEngineEnabled },
  brandScope,
  isLoading,
  onChange,
}: ComposerControlsProps) {
  const emptyTooltip = (noun: string, count: number) =>
    emptyOptionsTooltip(noun, count, brandScope);
  const adsStyle = styleSelectionOf(selected.adsStyle);
  const emailStyle = styleSelectionOf(selected.emailStyle);
  return (
    <Row gap={1}>
      {channels.ad && (
        <AdsPicker
          style={{
            value: adsStyle,
            onChange: (adsStyle) => onChange({ adsStyle }),
            options: options.adStyles,
            label: styleSelectionLabel(adsStyle, options.adStyles),
            isLoading,
            disabledTooltip: emptyTooltip("styles", options.adStyles.length),
          }}
          templates={{
            brandScope,
            options: options.adTemplates,
            drafts: options.adTemplateDrafts,
            value: {
              ids: selected.adTemplates.map(({ id }) => id),
              folderIds: selected.adTemplateFolders.map(({ id }) => id),
              folderBrandProfileId: selected.adTemplateFolderBrandProfileId,
            },
            onChange: ({ ids, folderIds, folderBrandProfileId }) =>
              onChange({
                adTemplateIds: ids,
                adTemplateFolderIds: folderIds ?? [],
                adTemplateFolderBrandProfileId: folderBrandProfileId,
              }),
            isLoading,
            disabledTooltip: emptyTooltip(
              "ad templates",
              options.adTemplates.length +
                options.adTemplateDrafts.length +
                options.adTemplateFolders.length,
            ),
          }}
          creativeBoost={
            isCreativeBoostEnabled
              ? {
                  value: selected.creativeBoost,
                  onChange: (creativeBoost) => onChange({ creativeBoost }),
                }
              : undefined
          }
          videoEngine={
            isVideoEngineEnabled
              ? {
                  value: selected.useVideoEngine,
                  onChange: (useVideoEngine) => onChange({ useVideoEngine }),
                }
              : undefined
          }
        />
      )}
      {channels.email && (
        <EmailPicker
          brandProfileIds={brandScope}
          style={{
            value: emailStyle,
            onChange: (emailStyle) => onChange({ emailStyle }),
            options: options.emailStyles,
            label: styleSelectionLabel(emailStyle, options.emailStyles),
            isLoading,
            disabledTooltip: emptyTooltip("styles", options.emailStyles.length),
          }}
          templates={{
            value: selected.emailTemplates,
            onChange: (emailTemplates) => onChange({ emailTemplates }),
          }}
          components={{
            value: selected.emailComponents,
            onChange: (emailComponents) => onChange({ emailComponents }),
          }}
        />
      )}
    </Row>
  );
}
